import React, { useEffect, useState } from "react";
import { Pencil, Trash2, Plus, X, Sparkles } from "lucide-react";
import { toast } from "react-toastify";
import {
  getAmenities,
  addAmenity,
  updateAmenity,
  deleteAmenity,
} from "../../../services/api";
import "./AmenitiesManagement.css";

export default function Amenities() {
  const [items, setItems] = useState([]);
  const [name, setName] = useState("");
  const [editing, setEditing] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Delete modal
  const [deleteId, setDeleteId] = useState(null);
  const [deleteName, setDeleteName] = useState("");
  const [deleting, setDeleting] = useState(false);

  const refresh = async () => {
    setLoading(true);
    try {
      const list = await getAmenities();
      setItems(list || []);
    } catch (err) {
      toast.error(err.message || "Failed to load amenities");
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
  }, []);

  async function submit(e) {
    e.preventDefault();
    if (!name.trim()) {
      setError("Amenity name is required");
      return;
    }
    setError("");
    setSaving(true);

    try {
      if (editing) {
        const res = await updateAmenity(editing, { name });
        toast.success(res.message || "Amenity updated");
      } else {
        const res = await addAmenity({ name });
        toast.success(res.message || "Amenity added");
      }
      setName("");
      setEditing(null);
      await refresh();
    } catch (err) {
      toast.error(err.message || "Save failed");
    } finally {
      setSaving(false);
    }
  }

  function openDeleteModal(a) {
    setDeleteId(a.id);
    setDeleteName(a.name || "this amenity");
  }

  function closeDeleteModal() {
    if (deleting) return;
    setDeleteId(null);
    setDeleteName("");
  }

  async function confirmDelete() {
    if (!deleteId) return;
    setDeleting(true);
    try {
      await deleteAmenity(deleteId);
      toast.success("Amenity deleted");
      setDeleteId(null);
      setDeleteName("");
      await refresh();
    } catch (err) {
      toast.error(err.message || "Delete failed");
    } finally {
      setDeleting(false);
    }
  }

  function onEdit(a) {
    setEditing(a.id);
    setName(a.name);
    setError("");
  }

  function onCancel() {
    setEditing(null);
    setName("");
    setError("");
  }

  return (
    <div className="amn">
      <div className="amn-head">
        <div>
          <div className="amn-eyebrow">Master data</div>
          <h1 className="amn-title">Amenities</h1>
          <p className="amn-sub">
            Master list used in the property amenities step — {items.length}{" "}
            total.
          </p>
        </div>
      </div>

      <form onSubmit={submit} className="amn-card">
        <div className="amn-card-head">
          {editing ? "Edit amenity" : "Add new amenity"}
        </div>

        <div className="amn-row">
          <div className="amn-field">
            <label className="amn-label">Amenity name</label>
            <input
              className="amn-input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Rooftop Infinity Pool"
              disabled={saving}
            />
            {error && <p className="amn-err">{error}</p>}
          </div>

          <button type="submit" className="amn-btn" disabled={saving}>
            <Plus className="h-4 w-4" />
            {saving ? "Saving…" : editing ? "Update" : "Add"}
          </button>

          {editing && (
            <button
              type="button"
              className="amn-btn-ghost"
              onClick={onCancel}
              disabled={saving}
            >
              <X className="h-4 w-4" />
              Cancel
            </button>
          )}
        </div>

        {!loading && items.length > 0 && (
          <div className="amn-chips">
            {items.map((a) => (
              <span key={a.id} className="amn-chip">
                <Sparkles className="h-3.5 w-3.5 amn-chip-ico" />
                {a.name}
              </span>
            ))}
          </div>
        )}
      </form>

      <div className="amn-table-wrap">
        <table className="amn-table">
          <thead>
            <tr>
              <th>Amenity</th>
              <th className="text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr>
                <td colSpan={2} className="amn-empty">
                  Loading amenities…
                </td>
              </tr>
            )}

            {!loading &&
              items.map((a) => (
                <tr key={a.id}>
                  <td className="amn-name">{a.name}</td>
                  <td>
                    <div className="amn-row-actions">
                      <button
                        type="button"
                        className="icon-btn"
                        title="Edit"
                        onClick={() => onEdit(a)}
                      >
                        <Pencil className="h-4 w-4" />
                      </button>
                      <button
                        type="button"
                        className="icon-btn icon-btn--danger"
                        title="Delete"
                        onClick={() => openDeleteModal(a)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}

            {!loading && items.length === 0 && (
              <tr>
                <td colSpan={2} className="amn-empty">
                  No amenities added yet.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Delete confirmation modal */}
      {deleteId && (
        <div className="amn-modal-backdrop" role="dialog" aria-modal="true">
          <div className="amn-modal">
            <h3 className="amn-modal-title">Delete amenity?</h3>
            <p className="amn-modal-text">
              Are you sure you want to delete <strong>{deleteName}</strong>?
              This action cannot be undone.
            </p>
            <div className="amn-modal-actions">
              <button
                type="button"
                className="amn-btn-ghost"
                onClick={closeDeleteModal}
                disabled={deleting}
              >
                Cancel
              </button>
              <button
                type="button"
                className="amn-btn-danger"
                onClick={confirmDelete}
                disabled={deleting}
              >
                {deleting ? "Deleting…" : "Yes, delete"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}